import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, ClipboardCheck, Search, Lock, ChevronRight, FileText, ShieldAlert, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { StatusChip, OverdueChip, CATEGORY_META, fmtDate } from './issueMeta';
import IssueHistoryDialog from './IssueHistoryDialog';
import MinutesReviewDialog from './MinutesReviewDialog';
import { apiErrorText, openVaultFile } from '@/lib/vaultUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const ORDER = { open: 0, no_update: 1, progress: 2, resolved_reported: 3, resolved_verified: 4 };

function IssueCard({ issue, member, onOpen }) {
  return (
    <Card className="transition-colors hover:border-foreground/30" data-testid={`pp-issue-${issue.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            {issue.safety && <ShieldAlert className="h-3.5 w-3.5 text-red-600 shrink-0" title="Safety-related" />}
            {issue.title}
          </h4>
          <div className="flex gap-1 shrink-0 flex-wrap justify-end">
            {issue.overdue && <OverdueChip testid={`pp-issue-overdue-${issue.id}`} />}
            <StatusChip status={issue.status} testid={`pp-issue-status-${issue.id}`} />
          </div>
        </div>
        {member ? (
          <>
            <p className="text-sm text-muted-foreground mt-1.5">{issue.latest_position}</p>
            <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {issue.last_meeting_date && <span>Last update: {fmtDate(issue.last_mentioned_meeting_date || issue.last_meeting_date)}</span>}
              {(issue.next_deadline || issue.deadline_text) && <span>Target: {fmtDate(issue.next_deadline) || issue.deadline_text}</span>}
              {issue.next_step && <span>Next: {issue.next_step}</span>}
            </div>
            <Button variant="ghost" size="sm" className="mt-2 h-7 px-0 text-xs" onClick={() => onOpen(issue.id)} data-testid={`pp-issue-history-${issue.id}`}>
              History & source <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </>
        ) : (
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><Lock className="h-3 w-3" />Details for verified members</p>
        )}
      </CardContent>
    </Card>
  );
}

function ApproverBar({ data, uploading, onPick, onReview }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed p-3" data-testid="pp-approver-bar">
      <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">Representatives: upload each meeting's minutes. AI drafts the changes; you review before anything is published.</p>
      <Button size="sm" variant="outline" onClick={onPick} disabled={uploading} data-testid="pp-upload-minutes-btn">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}Upload minutes
      </Button>
      <Button size="sm" onClick={onReview} variant={data.pending_reviews ? 'default' : 'ghost'} data-testid="pp-review-btn">
        <ClipboardCheck className="h-4 w-4 mr-1" />Review{data.pending_reviews ? ` (${data.pending_reviews})` : ''}
      </Button>
    </div>
  );
}

export default function ProjectIssuesTracker({ projectId, onOpenAuth, lockCategory }) {
  const { token } = useAuth();
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(lockCategory || 'all');
  const [q, setQ] = useState('');
  const [historyId, setHistoryId] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/api/post-oc/${projectId}/issues`, { headers }).then((r) => setData(r.data)).catch(() => setData({ issues: [] }));
  useEffect(() => { if (projectId) load(); }, [projectId, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/post-oc/${projectId}/minutes`, fd, { headers });
      toast.success('Minutes saved. AI is comparing them with earlier meetings; open Review in about a minute.');
      load();
      setReviewOpen(true);
    } catch (e) { toast.error(apiErrorText(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (!data) return <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" />;
  const issues = (data.issues || []).filter((i) => !lockCategory || i.category === lockCategory);
  const cats = Object.keys(CATEGORY_META).filter((k) => issues.some((i) => i.category === k));
  const shown = issues
    .filter((i) => (cat === 'all' || i.category === cat) && (!q || i.title.toLowerCase().includes(q.toLowerCase()) || (i.latest_position || '').toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => (b.overdue - a.overdue) || (b.safety - a.safety) || (ORDER[a.status] - ORDER[b.status]));
  const n = (f) => issues.filter(f).length;

  return (
    <div className="space-y-3" data-testid="pp-tracker">
      <p className="text-sm" data-testid="pp-tracker-summary">
        {data.last_meeting?.meeting_date ? (
          <>Based on minutes of <b>{fmtDate(data.last_meeting.meeting_date)}</b> · {issues.length} issues · {n((i) => i.overdue)} overdue · {n((i) => i.status === 'no_update')} with no update · {n((i) => i.status === 'resolved_verified')} verified resolved</>
        ) : 'No meeting minutes have been published for this project yet.'}
      </p>
      {data.is_member && data.last_meeting?.storage_path && (
        <button type="button" onClick={() => openVaultFile(data.last_meeting.storage_path).catch(() => toast.error('Could not open the minutes'))}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="pp-latest-minutes-link">
          <FileText className="h-3 w-3" />Open the latest minutes
        </button>
      )}
      {data.can_review && (
        <>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" onChange={(e) => upload(e.target.files?.[0])} data-testid="pp-minutes-file-input" />
          <ApproverBar data={data} uploading={uploading} onPick={() => fileRef.current?.click()} onReview={() => setReviewOpen(true)} />
        </>
      )}
      {issues.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {(lockCategory ? [] : ['all', ...cats]).map((k) => (
            <button key={k} type="button" onClick={() => setCat(k)} data-testid={`pp-filter-${k}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${cat === k ? 'bg-foreground text-background border-foreground' : 'hover:bg-muted'}`}>
              {k === 'all' ? `All (${issues.length})` : `${CATEGORY_META[k].label} (${n((i) => i.category === k)})`}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-56">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search issues" className="h-8 pl-8 text-xs" data-testid="pp-search" />
          </div>
        </div>
      )}
      {!data.is_member && issues.length > 0 && (
        <button type="button" onClick={() => onOpenAuth?.()} className="text-xs text-primary hover:underline" data-testid="pp-member-cta">
          Verified owners of this project see each issue's latest position, deadlines and the source minutes. Sign in or verify →
        </button>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {shown.map((i) => <IssueCard key={i.id} issue={i} member={data.is_member} onOpen={setHistoryId} />)}
      </div>
      <IssueHistoryDialog issueId={historyId} canReview={data.can_review} headers={headers} onOpenChange={(o) => !o && setHistoryId(null)} onChanged={load} />
      {data.can_review && <MinutesReviewDialog projectId={projectId} open={reviewOpen} onOpenChange={setReviewOpen} headers={headers} onPublished={load} />}
    </div>
  );
}
